import { Storage } from "@capacitor/storage";

/**
 * Installation ID (device binding).
 *
 * Identificador propio del dispositivo: un UUID que se genera la primera vez que
 * arranca la app y se persiste. Sirve para mitigar la marcacion por terceros
 * ("buddy punching"): el backend ata cada operario al dispositivo desde el que
 * inicia sesion y detecta cuando el codigo se usa desde otro celular.
 *
 * No usamos IMEI ni MAC: en Android 10+/iOS ya no son accesibles para apps
 * normales (permisos de sistema) y la MAC viene randomizada. Un UUID propio es
 * estable, no requiere permisos y no es dato sensible.
 *
 * Persistencia en dos capas:
 *  - localStorage: acceso SINCRONO, lo lee el interceptor de axios en cada request.
 *  - Capacitor Storage: durable a nivel nativo; sobrevive a limpiezas de localStorage.
 */

const DEVICE_ID_KEY = "deviceId";

// Vigencia larga de la cookie de respaldo (~2 años). Se refresca en cada lectura.
const COOKIE_MAX_AGE = 60 * 60 * 24 * 730;

// Caché en memoria del ID vigente de esta sesión. Evita que, ante un problema de
// storage, cada request genere un UUID distinto (falsos "cambios de dispositivo").
// initDeviceId() la sincroniza con la capa durable al arrancar.
let cachedId: string | null = null;

/**
 * Respaldo en COOKIE (lectura/escritura SINCRONA), como tercera capa de persistencia.
 * Motivo: en navegadores moviles el localStorage no siempre sobrevive entre aperturas
 * de la app (accesos directos/PWA con storage efimero), y ahi el UUID se regeneraba en
 * cada login. La cookie da otra chance de recuperar el MISMO id sin acuñar uno nuevo.
 * Es de primera parte (mismo origen del SPA), no es secreto y no viaja al backend: solo
 * la lee el JS para mandarla en el header X-Device-Id.
 */
function leerCookie(nombre: string): string | null {
  try {
    const prefijo = `${nombre}=`;
    const partes = (document.cookie || "").split(";");
    for (const parte of partes) {
      const p = parte.trim();
      if (p.startsWith(prefijo)) return decodeURIComponent(p.substring(prefijo.length));
    }
  } catch {
    /* ignore: cookies deshabilitadas o document no disponible */
  }
  return null;
}

function escribirCookie(nombre: string, valor: string): void {
  try {
    const secure =
      typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${nombre}=${encodeURIComponent(valor)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* ignore */
  }
}

/** Genera un UUID v4. Prioriza crypto (seguro); cae a getRandomValues y por ultimo Math.random. */
function generarUUID(): string {
  const c: any = typeof crypto !== "undefined" ? crypto : undefined;

  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }

  if (c && typeof c.getRandomValues === "function") {
    const b = new Uint8Array(16);
    c.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40; // version 4
    b[8] = (b[8] & 0x3f) | 0x80; // variante
    const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }

  // Ultimo recurso (no criptografico, solo por compatibilidad extrema)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Devuelve el Installation ID de forma SINCRONA (para el interceptor de axios).
 * Si no existe en localStorage lo genera al vuelo y lo persiste (en background en
 * la capa nativa). Nunca lanza: ante cualquier problema devuelve un id de sesion.
 */
export function getDeviceIdSync(): string {
  // Si ya lo resolvimos en esta sesión (incluida la reconciliación durable), usarlo.
  if (cachedId) return cachedId;
  try {
    // 1) localStorage. 2) si está vacío (típico en navegador móvil que no retiene
    // storage entre aperturas), intentamos recuperar el MISMO id desde la cookie
    // ANTES de acuñar uno nuevo, para no generar falsos "cambios de dispositivo".
    let id = localStorage.getItem(DEVICE_ID_KEY) || leerCookie(DEVICE_ID_KEY);
    if (!id) {
      id = generarUUID();
      // OJO: NO escribimos en Capacitor Storage acá. Si el localStorage se limpió
      // pero la capa durable conserva el ID, initDeviceId() lo va a restaurar; si
      // acá lo pisáramos, perderíamos ese ID durable y generaríamos un cambio falso.
    }
    // Persistimos/refrescamos las dos capas síncronas (localStorage + cookie).
    try {
      localStorage.setItem(DEVICE_ID_KEY, id);
    } catch {
      /* ignore */
    }
    escribirCookie(DEVICE_ID_KEY, id);
    cachedId = id;
    return id;
  } catch {
    // localStorage no disponible: la cookie es el respaldo; si tampoco, uno efímero
    // cacheado, para no mandar un id distinto en cada request.
    if (!cachedId) {
      cachedId = leerCookie(DEVICE_ID_KEY) || generarUUID();
      escribirCookie(DEVICE_ID_KEY, cachedId);
    }
    return cachedId;
  }
}

/**
 * Reconcilia el Installation ID entre la capa durable (Capacitor Storage) y
 * localStorage al arrancar la app. Si la capa nativa ya tiene un id (por ejemplo,
 * porque se limpio localStorage), lo restaura; si no, siembra el actual.
 * Llamar una vez al inicio (App.tsx). Es best-effort: nunca lanza.
 */
export async function initDeviceId(): Promise<string> {
  try {
    const durable = await Storage.get({ key: DEVICE_ID_KEY });
    if (durable && durable.value) {
      // La capa durable manda: la reflejamos en localStorage, cookie y la caché, aunque
      // getDeviceIdSync ya hubiera generado uno efímero antes de esta reconciliación.
      cachedId = durable.value;
      try {
        localStorage.setItem(DEVICE_ID_KEY, durable.value);
      } catch {
        /* ignore */
      }
      escribirCookie(DEVICE_ID_KEY, durable.value);
      return durable.value;
    }
  } catch {
    /* ignore: seguimos con el camino sincrono */
  }

  // No había ID durable: sembramos con el actual (o uno nuevo) y lo persistimos.
  const id = getDeviceIdSync();
  cachedId = id;
  try {
    await Storage.set({ key: DEVICE_ID_KEY, value: id });
  } catch {
    /* ignore */
  }
  return id;
}
